import { motion } from 'framer-motion';
import { useState, useEffect, useMemo } from 'react';
import { 
  Users, DollarSign, TrendingUp, Calendar, CreditCard, 
  RefreshCw, MapPin, BarChart3, Repeat, Ticket, Eye, EyeOff
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { useAdminAnalytics, Timeframe } from '../api/useAdminAnalytics';
import { Loader } from '@/components/Loader';

// Color palette
const COLORS = {
  primary: '#8B5CF6',
  secondary: '#06B6D4',
  accent: '#F59E0B',
  success: '#10B981',
  danger: '#EF4444',
  pink: '#EC4899',
  indigo: '#6366F1',
  lime: '#84CC16',
};

const CITY_COLORS = [
  COLORS.primary,
  COLORS.secondary,
  COLORS.accent,
  COLORS.success,
  COLORS.danger,
  COLORS.pink,
  COLORS.indigo,
  COLORS.lime,
];

const TIMEFRAMES: { value: Timeframe; label: string }[] = [
  { value: '7d', label: '7 Days' },
  { value: '14d', label: '14 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '180d', label: '6 Months' },
  { value: '365d', label: '1 Year' },
  { value: 'all', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

// Helper to get default date range (last 30 days)
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
};

export default function AdminAnalytics() {
  const [timeframe, setTimeframe] = useState<Timeframe>('30d');
  const [selectedCity, setSelectedCity] = useState<string>('all');
  const [visibleCities, setVisibleCities] = useState<Set<string>>(new Set());
  const [customStartDate, setCustomStartDate] = useState<string>(getDefaultDateRange().start);
  const [customEndDate, setCustomEndDate] = useState<string>(getDefaultDateRange().end);

  const { data: analyticsData, isLoading, refetch } = useAdminAnalytics({
    timeframe,
    city: selectedCity,
    startDate: customStartDate,
    endDate: customEndDate,
  });

  const data = analyticsData?.data;
  const summary = data?.summary;
  const cities = useMemo(() => data?.cities || [], [data?.cities]);

  // Initialize visible cities when cities data loads
  useEffect(() => {
    if (cities.length > 0 && visibleCities.size === 0) {
      setVisibleCities(new Set(cities.map(c => c.id)));
    }
  }, [cities, visibleCities.size]);

  const toggleCity = (cityId: string) => {
    setVisibleCities(prev => {
      const next = new Set(prev);
      if (next.has(cityId)) {
        next.delete(cityId);
      } else {
        next.add(cityId);
      }
      return next;
    });
  };

  const toggleAllCities = (show: boolean) => {
    if (show) {
      setVisibleCities(new Set(cities.map(c => c.id)));
    } else {
      setVisibleCities(new Set());
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatDateLong = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl md:text-5xl font-bold mb-2">Analytics</h1>
            <p className="text-dark-muted text-lg">
              Advanced signup, payment & booking analytics
            </p>
          </div>
          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="btn btn-ghost self-start"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card mb-8"
      >
        <div className="flex flex-wrap items-center gap-6">
          {/* Timeframe Filter */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-dark-muted">Timeframe</label>
            <div className="flex flex-wrap gap-2">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf.value}
                  onClick={() => setTimeframe(tf.value)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    timeframe === tf.value
                      ? 'bg-primary-500 text-white'
                      : 'bg-dark-border text-dark-muted hover:bg-dark-border/70'
                  }`}
                >
                  {tf.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Date Range */}
          {timeframe === 'custom' && (
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-dark-muted">Date Range</label>
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  max={customEndDate}
                  className="bg-dark-border text-white px-3 py-1.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none text-sm"
                />
                <span className="text-dark-muted">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  max={new Date().toISOString().split('T')[0]}
                  className="bg-dark-border text-white px-3 py-1.5 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none text-sm"
                />
              </div>
            </div>
          )}

          {/* City Filter */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-dark-muted">City</label>
            <select
              value={selectedCity}
              onChange={(e) => setSelectedCity(e.target.value)}
              className="bg-dark-border text-white px-4 py-2 rounded-lg border border-dark-border focus:border-primary-500 focus:outline-none"
            >
              <option value="all">All Cities</option>
              {cities.map((city) => (
                <option key={city.id} value={city.id}>
                  {city.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </motion.div>

      {/* Loading State */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Loader />
        </div>
      ) : (
        <>
      {/* Summary Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{summary?.totalSignups?.toLocaleString() || 0}</p>
              <p className="text-dark-muted text-xs">Signups</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatCurrency(summary?.totalRevenue || 0)}</p>
              <p className="text-dark-muted text-xs">Revenue</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Repeat className="w-5 h-5 text-cyan-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{summary?.activeSubscriptions?.toLocaleString() || 0}</p>
              <p className="text-dark-muted text-xs">Active Subs</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card"
        >
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <Ticket className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-2xl font-bold">{summary?.totalActiveBookings?.toLocaleString() || 0}</p>
              <p className="text-dark-muted text-xs">Active Bookings</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Detailed Stats Row */}
      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35 }}
          className="card"
        >
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <Repeat className="w-5 h-5 text-cyan-500" />
            Subscriptions
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">New</span>
              <span className="font-semibold text-green-400">+{summary?.newSubscriptions || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Cancelled</span>
              <span className="font-semibold text-red-400">-{summary?.cancelledSubscriptions || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Revenue</span>
              <span className="font-semibold">{formatCurrency(summary?.subscriptionRevenue || 0)}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card"
        >
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-amber-500" />
            One-Time Purchases
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Purchases</span>
              <span className="font-semibold">{summary?.oneTimePayments || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Active Credits</span>
              <span className="font-semibold text-green-400">{summary?.activeOneTime || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Revenue</span>
              <span className="font-semibold">{formatCurrency(summary?.oneTimeRevenue || 0)}</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="card"
        >
          <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-green-500" />
            Payment Stats
          </h3>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Total Payments</span>
              <span className="font-semibold">{summary?.totalPayments || 0}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Avg Per Payment</span>
              <span className="font-semibold">
                {formatCurrency(summary?.totalPayments ? (summary?.totalRevenue || 0) / summary.totalPayments : 0)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-dark-muted text-sm">Conversion</span>
              <span className="font-semibold">
                {summary?.totalSignups ? ((summary?.totalPayments || 0) / summary.totalSignups * 100).toFixed(1) : 0}%
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Signups Chart */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="card mb-8"
      >
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Users className="w-5 h-5 text-primary-500" />
          Signups Over Time
        </h2>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.signups?.perDay || []}>
              <defs>
                <linearGradient id="signupGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3}/>
                  <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={formatDate}
              />
              <YAxis 
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB'
                }}
                labelFormatter={formatDateLong}
              />
              <Area 
                type="monotone" 
                dataKey="count" 
                stroke={COLORS.primary}
                strokeWidth={2}
                fill="url(#signupGradient)"
                name="Signups"
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Signups by City */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.55 }}
        className="card mb-8"
      >
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-primary-500" />
          Signups by City
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.signups?.perCity?.slice(0, 8) || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="cityName" 
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  width={100}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                />
                <Bar dataKey="count" fill={COLORS.primary} name="Signups" radius={[0, 4, 4, 0]}>
                  {(data?.signups?.perCity?.slice(0, 8) || []).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CITY_COLORS[index % CITY_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Pie Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data?.signups?.perCity?.slice(0, 6) || []}
                  cx="50%"
                  cy="50%"
                  innerRadius={40}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="count"
                  nameKey="cityName"
                  label={({ cityName, percent }) => `${cityName} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={false}
                >
                  {(data?.signups?.perCity?.slice(0, 6) || []).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CITY_COLORS[index % CITY_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </motion.div>

      {/* Revenue Chart - Stacked by City */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-500" />
            Revenue Over Time by City
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleAllCities(true)}
              className="text-xs px-2 py-1 rounded bg-dark-border hover:bg-dark-border/70 text-dark-muted hover:text-white transition-colors flex items-center gap-1"
            >
              <Eye className="w-3 h-3" />
              All
            </button>
            <button
              onClick={() => toggleAllCities(false)}
              className="text-xs px-2 py-1 rounded bg-dark-border hover:bg-dark-border/70 text-dark-muted hover:text-white transition-colors flex items-center gap-1"
            >
              <EyeOff className="w-3 h-3" />
              None
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {cities.map((city, index) => {
            const isVisible = visibleCities.has(city.id);
            return (
              <button
                key={city.id}
                onClick={() => toggleCity(city.id)}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-all ${
                  isVisible 
                    ? 'bg-dark-border text-white' 
                    : 'bg-dark-border/30 text-dark-muted line-through'
                }`}
              >
                <div 
                  className="w-2.5 h-2.5 rounded-sm transition-opacity" 
                  style={{ 
                    backgroundColor: CITY_COLORS[index % CITY_COLORS.length],
                    opacity: isVisible ? 1 : 0.3
                  }}
                />
                {city.name}
              </button>
            );
          })}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.payments?.perDayStacked || []}>
              <defs>
                {cities.filter(c => visibleCities.has(c.id)).map((city) => {
                  const originalIndex = cities.findIndex(c => c.id === city.id);
                  return (
                    <linearGradient key={city.id} id={`revenueGradient-${city.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CITY_COLORS[originalIndex % CITY_COLORS.length]} stopOpacity={0.8}/>
                      <stop offset="95%" stopColor={CITY_COLORS[originalIndex % CITY_COLORS.length]} stopOpacity={0.3}/>
                    </linearGradient>
                  );
                })}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={formatDate}
              />
              <YAxis 
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB'
                }}
                labelFormatter={formatDateLong}
                formatter={(value: number, name: string) => {
                  const cityName = cities.find(c => c.id === name)?.name || name;
                  return [`$${value.toFixed(2)}`, cityName];
                }}
              />
              {cities.filter(c => visibleCities.has(c.id)).map((city) => {
                const originalIndex = cities.findIndex(c => c.id === city.id);
                return (
                  <Area 
                    key={city.id}
                    type="linear" 
                    dataKey={city.id}
                    stackId="revenue"
                    stroke={CITY_COLORS[originalIndex % CITY_COLORS.length]}
                    strokeWidth={1}
                    fill={`url(#revenueGradient-${city.id})`}
                    name={city.id}
                  />
                );
              })}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Subscriptions Chart - Stacked by City */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
        className="card mb-8"
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Repeat className="w-5 h-5 text-cyan-500" />
            New Subscriptions Over Time by City
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={() => toggleAllCities(true)}
              className="text-xs px-2 py-1 rounded bg-dark-border hover:bg-dark-border/70 text-dark-muted hover:text-white transition-colors flex items-center gap-1"
            >
              <Eye className="w-3 h-3" />
              All
            </button>
            <button
              onClick={() => toggleAllCities(false)}
              className="text-xs px-2 py-1 rounded bg-dark-border hover:bg-dark-border/70 text-dark-muted hover:text-white transition-colors flex items-center gap-1"
            >
              <EyeOff className="w-3 h-3" />
              None
            </button>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mb-4">
          {cities.map((city, index) => {
            const isVisible = visibleCities.has(city.id);
            return (
              <button
                key={city.id}
                onClick={() => toggleCity(city.id)}
                className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md transition-all ${
                  isVisible 
                    ? 'bg-dark-border text-white' 
                    : 'bg-dark-border/30 text-dark-muted line-through'
                }`}
              >
                <div 
                  className="w-2.5 h-2.5 rounded-sm transition-opacity" 
                  style={{ 
                    backgroundColor: CITY_COLORS[index % CITY_COLORS.length],
                    opacity: isVisible ? 1 : 0.3
                  }}
                />
                {city.name}
              </button>
            );
          })}
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data?.subscriptions?.perDayStacked || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis 
                dataKey="date" 
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                tickFormatter={formatDate}
              />
              <YAxis 
                stroke="#9CA3AF"
                tick={{ fill: '#9CA3AF', fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1F2937', 
                  border: '1px solid #374151',
                  borderRadius: '0.5rem',
                  color: '#F9FAFB'
                }}
                labelFormatter={formatDateLong}
                formatter={(value: number, name: string) => {
                  const cityName = cities.find(c => c.id === name)?.name || name;
                  return [value, cityName];
                }}
              />
              {cities.filter(c => visibleCities.has(c.id)).map((city) => {
                const originalIndex = cities.findIndex(c => c.id === city.id);
                return (
                  <Bar 
                    key={city.id}
                    dataKey={city.id}
                    stackId="subscriptions"
                    fill={CITY_COLORS[originalIndex % CITY_COLORS.length]}
                    name={city.id}
                  />
                );
              })}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </motion.div>

      {/* Bookings Charts */}
      <div className="grid md:grid-cols-2 gap-8 mb-8">
        {/* Bookings by Creation Date */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="card"
        >
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            Bookings by Creation Date
          </h2>
          <p className="text-dark-muted text-sm mb-4">When users made their reservations</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data?.bookings?.createdPerDay || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  tickFormatter={formatDate}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={formatDateLong}
                />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke={COLORS.accent}
                  strokeWidth={2}
                  dot={{ r: 2 }}
                  activeDot={{ r: 4 }}
                  name="Bookings Created"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Bookings by Event Date */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.75 }}
          className="card"
        >
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-pink-500" />
            Bookings by Event Date
          </h2>
          <p className="text-dark-muted text-sm mb-4">When dinners are scheduled to happen</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.bookings?.byEventDay?.filter(d => d.count > 0) || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis 
                  dataKey="date" 
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  tickFormatter={formatDate}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  allowDecimals={false}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                  labelFormatter={formatDateLong}
                />
                <Bar dataKey="count" fill={COLORS.pink} name="Bookings" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </motion.div>
      </div>

      {/* City Performance Comparison */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="card mb-8"
      >
        <h2 className="text-xl font-bold mb-2 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-primary-500" />
          City Performance Comparison
        </h2>
        <p className="text-dark-muted text-sm mb-4">
          Compare revenue, new subscribers, and active subscribers across cities for smarter ad spend allocation
        </p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-border">
                <th className="text-left py-3 px-4 text-dark-muted text-sm font-medium">City</th>
                <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">
                  <span className="flex items-center justify-end gap-1">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    Revenue
                  </span>
                </th>
                <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">
                  <span className="flex items-center justify-end gap-1">
                    <TrendingUp className="w-4 h-4 text-cyan-500" />
                    New Subs
                  </span>
                </th>
                <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">
                  <span className="flex items-center justify-end gap-1">
                    <Repeat className="w-4 h-4 text-primary-500" />
                    Active Subs
                  </span>
                </th>
                <th className="text-right py-3 px-4 text-dark-muted text-sm font-medium">Rev Share</th>
              </tr>
            </thead>
            <tbody>
              {(data?.cityBreakdown || [])
                .filter(city => city.revenue > 0 || city.newSubscribers > 0 || city.activeSubscribers > 0)
                .map((city, index) => {
                  const totalRevenue = (data?.cityBreakdown || []).reduce((sum, c) => sum + c.revenue, 0);
                  const revenueShare = totalRevenue > 0 ? (city.revenue / totalRevenue * 100).toFixed(1) : '0';
                  return (
                    <tr key={city.cityId} className="border-b border-dark-border/50 hover:bg-dark-border/20 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: CITY_COLORS[index % CITY_COLORS.length] }}
                          />
                          <span className="font-medium">{city.cityName}</span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="font-semibold text-green-400">{formatCurrency(city.revenue)}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className={`font-semibold ${city.newSubscribers > 0 ? 'text-cyan-400' : 'text-dark-muted'}`}>
                          {city.newSubscribers > 0 ? `+${city.newSubscribers}` : '0'}
                        </span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <span className="font-semibold">{city.activeSubscribers}</span>
                      </td>
                      <td className="text-right py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <div className="w-16 h-2 bg-dark-border rounded-full overflow-hidden">
                            <div 
                              className="h-full rounded-full transition-all"
                              style={{ 
                                width: `${revenueShare}%`,
                                backgroundColor: CITY_COLORS[index % CITY_COLORS.length]
                              }}
                            />
                          </div>
                          <span className="text-dark-muted text-sm w-12 text-right">{revenueShare}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {/* Totals row */}
              {(data?.cityBreakdown || []).filter(c => c.revenue > 0 || c.newSubscribers > 0 || c.activeSubscribers > 0).length > 0 && (
                <tr className="bg-dark-border/30 font-semibold">
                  <td className="py-3 px-4">Total</td>
                  <td className="text-right py-3 px-4 text-green-400">
                    {formatCurrency((data?.cityBreakdown || []).reduce((sum, c) => sum + c.revenue, 0))}
                  </td>
                  <td className="text-right py-3 px-4 text-cyan-400">
                    +{(data?.cityBreakdown || []).reduce((sum, c) => sum + c.newSubscribers, 0)}
                  </td>
                  <td className="text-right py-3 px-4">
                    {(data?.cityBreakdown || []).reduce((sum, c) => sum + c.activeSubscribers, 0)}
                  </td>
                  <td className="text-right py-3 px-4 text-dark-muted">100%</td>
                </tr>
              )}
            </tbody>
          </table>
          {(data?.cityBreakdown || []).filter(c => c.revenue > 0 || c.newSubscribers > 0 || c.activeSubscribers > 0).length === 0 && (
            <div className="text-center py-8 text-dark-muted">
              No city data available for the selected timeframe
            </div>
          )}
        </div>
      </motion.div>

      {/* Bookings by City */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.85 }}
        className="card mb-8"
      >
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-amber-500" />
          Bookings by City
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Bar Chart */}
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.bookings?.perCity?.slice(0, 8) || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis type="number" stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                <YAxis 
                  type="category" 
                  dataKey="cityName" 
                  stroke="#9CA3AF"
                  tick={{ fill: '#9CA3AF', fontSize: 11 }}
                  width={100}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#1F2937', 
                    border: '1px solid #374151',
                    borderRadius: '0.5rem',
                    color: '#F9FAFB'
                  }}
                />
                <Bar dataKey="count" fill={COLORS.accent} name="Bookings" radius={[0, 4, 4, 0]}>
                  {(data?.bookings?.perCity?.slice(0, 8) || []).map((_, index) => (
                    <Cell key={`cell-${index}`} fill={CITY_COLORS[index % CITY_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {/* Table */}
          <div className="overflow-y-auto max-h-64">
            <table className="w-full">
              <thead className="sticky top-0 bg-dark-card">
                <tr className="border-b border-dark-border">
                  <th className="text-left py-2 px-3 text-dark-muted text-sm font-medium">City</th>
                  <th className="text-right py-2 px-3 text-dark-muted text-sm font-medium">Bookings</th>
                  <th className="text-right py-2 px-3 text-dark-muted text-sm font-medium">Share</th>
                </tr>
              </thead>
              <tbody>
                {(data?.bookings?.perCity || []).map((city, index) => {
                  const total = (data?.bookings?.perCity || []).reduce((sum, c) => sum + c.count, 0);
                  const share = total > 0 ? (city.count / total * 100).toFixed(1) : '0';
                  return (
                    <tr key={city.cityId} className="border-b border-dark-border/50">
                      <td className="py-2 px-3">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-2.5 h-2.5 rounded-full" 
                            style={{ backgroundColor: CITY_COLORS[index % CITY_COLORS.length] }}
                          />
                          <span className="text-sm">{city.cityName}</span>
                        </div>
                      </td>
                      <td className="text-right py-2 px-3 font-semibold text-sm">{city.count}</td>
                      <td className="text-right py-2 px-3 text-dark-muted text-sm">{share}%</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </motion.div>

      {/* Date Range Info */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="text-center text-dark-muted text-sm mb-8"
      >
        Data from {data?.startDate ? new Date(data.startDate).toLocaleDateString() : '—'} to {data?.endDate ? new Date(data.endDate).toLocaleDateString() : '—'}
      </motion.div>
        </>
      )}
    </div>
  );
}

